import { ScreenFrame } from '@words-happen/screens';
import { ChevronLeftIcon, Lexi, type LexiMood, WordsHappenButton } from '@words-happen/ui-components';
import type React from 'react';
import styles from './message-screen.module.scss';

export interface MessageScreenProps {
  title: string;
  /** Lexi's expression above the title. */
  mood?: LexiMood;
  /** Supporting copy under the title. */
  children?: React.ReactNode;
  /**
   * The way onwards, e.g. a link or button styled with `messageScreenActionClassName`, followed by any secondary
   * links and a status line (`messageScreenStatusClassName`).
   */
  action?: React.ReactNode;
  /** Adds a 44×44 back chevron ("Back") above the message, e.g. back to /sign-in. */
  onBack?: () => void;
}

/** Class for the link or button passed as a MessageScreen's `action`: a full-width, primary call to action. */
export const messageScreenActionClassName = styles.action;

/** Class for a short status line beside the action, e.g. a role="status" message after a resend. */
export const messageScreenStatusClassName = styles.status;

/**
 * Class for a column of several actions passed as `action`: library buttons (WordsHappenButton size="lg"), a
 * WordsHappenFormAlert, status lines and SignInPrompt links, stacked and centred.
 */
export const messageScreenActionsClassName = styles.actions;

/** Class for a smaller line of supporting copy among a MessageScreen's children, e.g. a deadline. */
export const messageScreenNoticeClassName = styles.notice;

/**
 * Class for a form passed as `action`: a full-width column of library fields (WordsHappenTextField,
 * WordsHappenPasswordField), a WordsHappenFormAlert and a WordsHappenButton size="lg", with labels aligned to the start.
 */
export const messageScreenFormClassName = styles.form;

/** A short message centred in the standard screen frame: Lexi, a title, supporting copy and one action. */
export const MessageScreen: React.FC<MessageScreenProps> = ({ title, mood = 'happy', children, action, onBack }) => (
  <ScreenFrame>
    {onBack ? (
      <header className={styles.topBar}>
        <WordsHappenButton
          size="sm"
          className={styles.back}
          aria-label="Back"
          icon={<ChevronLeftIcon size={22} />}
          onClick={onBack}
        />
      </header>
    ) : null}
    <main className={styles.main}>
      <Lexi mood={mood} size={96} />
      <h1 className={styles.title}>{title}</h1>
      {children ? <div className={styles.body}>{children}</div> : null}
      {action}
    </main>
  </ScreenFrame>
);
